import { memo, useState } from 'react';
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AssignmentTurnedIn from '@material-ui/icons/AssignmentTurnedIn';
import MoneyOffIcon from '@material-ui/icons/MoneyOff';
import ConfirmationModal from '../../components/ConfirmationModal';
import LoadingModal from '../../components/LoadingModal';
import { ReturnService } from '../../services';
import { useError } from '../../contexts/ErrorContext';
import { useRole } from '../../contexts/RolesContext';
import { RETURN_STATUS } from '../../utils/constants';

/**
 * `allowedFrom` mirrors the AASM events on P4's Return model: `process` runs on
 * issued returns only, `bounce` on issued or processed ones. A button whose
 * event does not apply to every selected return hides itself, so a return that
 * is already processed (or bounced) removes the matching action from the
 * toolbar instead of failing once the request is sent.
 */
export const RETURN_TRANSITIONS = {
    PROCESS: {
        stateEvent: 'process',
        allowedFrom: [RETURN_STATUS.ISSUED],
        tooltip: "Mark Returns as 'Processed'",
        confirmation: 'Are you sure you want to mark these returns as processed? This cannot be undone.',
        icon: <AssignmentTurnedIn />
    },
    BOUNCE: {
        stateEvent: 'bounce',
        allowedFrom: [RETURN_STATUS.ISSUED, RETURN_STATUS.PROCESSED],
        tooltip: "Mark Returns as 'Bounced'",
        confirmation: 'Are you sure you want to mark these returns as bounced? This cannot be undone.',
        icon: <MoneyOffIcon />
    }
};

const BulkReturnTransitionButton = ({ selectedRowIndices, returns, transition }) => {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const { isAdmin, isDailyProcessingWrite } = useRole();
    const { showError } = useError();

    const toggleConfirmation = () => setShowConfirmation(showConfirmation => !showConfirmation);

    // This runs on every render, not just on click, so a selection index that no
    // longer resolves must hide the button rather than throw and take the table
    // down with it.
    const selectedReturns = selectedRowIndices.map(rowId => returns[rowId]);
    const appliesToSelection = selectedReturns.length > 0 &&
        selectedReturns.every(returnItem => transition.allowedFrom.includes(returnItem?.status));

    if (!(isDailyProcessingWrite() || isAdmin()) || !appliesToSelection) {
        return null;
    }

    const handleTransitionClick = () => {
        toggleConfirmation();
        setShowLoadingModal(true);
        ReturnService.bulkTransitionReturns(selectedReturns.map(returnItem => returnItem.id), transition.stateEvent)
            .then(response => {
                // !FIXME: Do this better!
                window.location.reload();
            }).catch(showError)
            .finally(() => {
                setShowLoadingModal(false);
            });
    }

    return (
        <>
            <Tooltip title={transition.tooltip}>
                <IconButton aria-label={transition.tooltip} onClick={toggleConfirmation}>
                    {transition.icon}
                </IconButton>
            </Tooltip>
            <ConfirmationModal title="Confirm" message={transition.confirmation} onConfirm={handleTransitionClick} onCancel={toggleConfirmation} showConfirmationBox={showConfirmation}/>
            <LoadingModal open={showLoadingModal} />
        </>
    );
}

export default memo(BulkReturnTransitionButton);
